<template lang="pug">
.email-approval-page
  .ea-card
    .ea-header
      Icon(icon="tabler:medical-cross")
      span MC Approvals
      span.ea-header-count(v-if="!loading") {{ pendingCards.length }} pending

    .ea-loading(v-if="loading")
      b-spinner(variant="primary")
      p.text-muted.mt-2 Loading requests…

    .ea-error(v-else-if="loadError")
      Icon(icon="tabler:alert-triangle")
      p {{ loadError }}
      b-button(variant="outline-primary" size="sm" @click="load") Try again

    template(v-else)
      //- The request the email pointed at is no longer pending
      .ea-notice.info(v-if="linkedProcessed")
        Icon(icon="tabler:info-circle")
        span The request from your email ({{ linkedProcessed.client }}, {{ linkedProcessed.dates }}) is already #[strong {{ linkedProcessed.status }}] — nothing more to do for it.

      //- Bulk action
      .ea-bulk(v-if="pendingCards.length > 1")
        span.small.text-muted {{ allowedCount }} of {{ pendingCards.length }} can be approved by you
        b-button(size="sm" variant="success" :disabled="bulkRunning || !allowedCount" @click="approveAllAllowed")
          b-spinner(small v-if="bulkRunning")
          Icon(icon="tabler:checks" v-else)
          | Approve all ({{ allowedCount }})

      .ea-empty(v-if="!pendingCards.length && !linkedProcessed")
        Icon(icon="tabler:circle-check")
        p Nothing waiting for approval.
        p.ea-close You can close this page.
      .ea-empty(v-else-if="!pendingCards.length")
        p.ea-close No other requests are pending — you can close this page.

      //- One card per pending request; the deep-linked one is first and expanded
      .ea-req(v-for="c in pendingCards" :key="c.id" :class="{ 'ea-req--open': c.open, 'ea-req--linked': c.id === linkedId }")
        .ea-req-head(@click="toggle(c)")
          .ea-req-title
            strong {{ c.client }}
            span.ea-req-sub {{ c.dates }} · {{ c.typeLabel }}
            span.ea-req-sub(v-if="c.submittedBy") by {{ c.submittedBy }}
          .ea-req-state
            span.ea-badge.ok(v-if="c.result === 'approved'") Approved
            span.ea-badge.bad(v-else-if="c.result === 'rejected'") Rejected
            span.ea-badge.warn(v-else-if="c.estimate && c.estimate.approval && c.estimate.approval.allowed === false") Not yours to approve
            span.ea-badge.cn(v-else-if="c.estimate && c.estimate.willReceiveCreditNote") CN ~${{ Number(c.estimate.estimatedCredit || 0).toFixed(0) }}
            span.ea-badge.muted(v-else-if="c.estimate") No CN
            Icon(:icon="c.open ? 'tabler:chevron-up' : 'tabler:chevron-down'")

        .ea-req-body(v-if="c.open")
          .ea-row
            .ea-label Reason
            .ea-value {{ c.reason || '—' }}
          .ea-row
            .ea-label Submitted
            .ea-value {{ c.submittedOn }}
          .ea-row(v-if="c.proofPending")
            .ea-label Proof
            .ea-value.text-warning Not uploaded — staff hold the physical MC

          .ea-estimate(v-if="c.estimate")
            .ea-estimate-title
              Icon(icon="tabler:receipt-2")
              | Billing outcome
            p.mb-1(v-if="c.estimate.willReceiveCreditNote")
              strong Credit note: 
              | ~${{ Number(c.estimate.estimatedCredit || 0).toFixed(2) }} for {{ (c.estimate.creditableDates || []).length }} already-invoiced date(s)
            p.mb-1(v-else)
              strong No credit note — 
              | none of these dates are invoiced yet; they'll be excluded from the next invoice.
          .ea-estimate.muted(v-else-if="c.estimateLoading")
            b-spinner(small)
            span.ml-2 Checking invoices…

          .ea-docs
            .ea-estimate-title
              Icon(icon="tabler:paperclip")
              | MC document{{ c.documents.length === 1 ? '' : 's' }}
            .ea-loading.small(v-if="c.docsLoading")
              b-spinner(small)
              span.ml-2 Loading document…
            p.text-danger.small(v-else-if="c.docsError") {{ c.docsError }}
            p.text-muted.small(v-else-if="!c.documents.length") No document attached.
            .ea-doc(v-for="(d, i) in c.documents" :key="i")
              .ea-doc-name
                Icon(:icon="isImage(d) ? 'tabler:photo' : 'tabler:file-type-pdf'")
                span {{ d.filename }}
                a.ml-auto(:href="d.downloadUrl" target="_blank" rel="noopener") Open
              img.ea-doc-img(v-if="isImage(d)" :src="d.downloadUrl" :alt="d.filename")
              iframe.ea-doc-pdf(v-else-if="isPdf(d)" :src="d.downloadUrl" :title="d.filename")

          .ea-notice.warning(v-if="c.estimate && c.estimate.approval && c.estimate.approval.allowed === false")
            Icon(icon="tabler:lock")
            span {{ c.estimate.approval.reason || 'You are not authorised to approve this request.' }}

          .ea-done-inline(v-if="c.result")
            Icon(:icon="c.result === 'approved' ? 'tabler:circle-check' : 'tabler:circle-x'" :class="c.result")
            span {{ c.result === 'approved' ? 'Approved — the caregiver has been notified.' : 'Rejected — the caregiver has been notified.' }}

          template(v-else)
            .ea-reject(v-if="c.rejecting")
              label.ea-label Reason for rejection
              b-form-textarea(v-model="c.rejectReason" rows="3" placeholder="Tell the caregiver why…")
            .ea-actions
              template(v-if="!c.rejecting")
                b-button.ea-btn(variant="success" :disabled="!canApprove(c) || !!c.saving" @click="approve(c)")
                  b-spinner(small v-if="c.saving === 'approve'")
                  Icon(icon="tabler:check" v-else)
                  | Approve
                b-button.ea-btn(variant="outline-danger" :disabled="!!c.saving" @click="c.rejecting = true")
                  Icon(icon="tabler:x")
                  | Reject
              template(v-else)
                b-button.ea-btn(variant="danger" :disabled="c.rejectReason.trim().length < 3 || !!c.saving" @click="reject(c)")
                  b-spinner(small v-if="c.saving === 'reject'")
                  Icon(icon="tabler:x" v-else)
                  | Confirm rejection
                b-button.ea-btn(variant="outline-secondary" :disabled="!!c.saving" @click="c.rejecting = false") Back
            p.ea-error-text(v-if="c.actionError") {{ c.actionError }}

      .ea-footer(v-if="pendingCards.length")
        p.ea-close(v-if="pendingCards.every(c => c.result)") All done — you can close this page.
        b-button(variant="link" size="sm" @click="$router.push('/approval-requests')") Open the full approvals dashboard
</template>

<script>
import { Icon } from '@iconify/vue2';
import { mapActions } from 'vuex';
import axios from 'axios';
import dayjs from 'dayjs';
import { MAKEUP_REQUESTS_NAMESPACE, ACTIONS } from '@/store/Modules/MakeupRequests/types-makeup-requests';
import authModule from '@/store/authenticator';

const parseMeta = (raw) => { try { return raw ? JSON.parse(raw) : {}; } catch (e) { return {}; } };
const expandDates = (r) => {
  const m = parseMeta(r.crb5c_mcdaterange);
  if (Array.isArray(m.dates) && m.dates.length) return [...m.dates].sort();
  if (m.start && m.end) {
    const out = []; let cur = dayjs(m.start); const end = dayjs(m.end);
    while (!cur.isAfter(end, 'day')) { out.push(cur.format('YYYY-MM-DD')); cur = cur.add(1, 'day'); }
    return out;
  }
  return r.crb5c_originaldate ? [r.crb5c_originaldate.split('T')[0]] : [];
};
const summarise = (d) => {
  if (!d.length) return '—';
  if (d.length === 1) return dayjs(d[0]).format('ddd, D MMM YYYY');
  return `${dayjs(d[0]).format('D MMM')} – ${dayjs(d[d.length - 1]).format('D MMM YYYY')} (${d.length} days)`;
};

export default {
  name: 'EmailApprovalPage',
  components: { Icon },
  data() {
    return {
      loading: true,
      loadError: null,
      cards: [],
      linkedProcessed: null,
      bulkRunning: false,
    };
  },
  computed: {
    linkedId() { return this.$route.params.id || null; },
    // Deep-linked request first, then oldest submissions first.
    pendingCards() {
      return [...this.cards].sort((a, b) => (a.id === this.linkedId ? -1 : b.id === this.linkedId ? 1 : a.submittedTs - b.submittedTs));
    },
    allowedCount() {
      return this.cards.filter((c) => !c.result && c.estimate && c.estimate.approval && c.estimate.approval.allowed !== false).length;
    },
  },
  async mounted() {
    await authModule.ready();
    await this.load();
  },
  methods: {
    ...mapActions(MAKEUP_REQUESTS_NAMESPACE, {
      fetchSingle: ACTIONS.FETCH_SINGLE_REQUEST,
      getDocument: ACTIONS.GET_DOCUMENT,
      getCreditEstimate: ACTIONS.GET_CREDIT_ESTIMATE,
      approveRequest: ACTIONS.APPROVE_REQUEST,
      rejectRequest: ACTIONS.REJECT_REQUEST,
    }),
    isImage(d) { return (d.mimetype || '').toLowerCase().startsWith('image/'); },
    isPdf(d) { return (d.mimetype || '').toLowerCase().includes('pdf'); },
    canApprove(c) { return !c.result && !(c.estimate && c.estimate.approval && c.estimate.approval.allowed === false); },

    toCard(r) {
      const meta = parseMeta(r.crb5c_mcdaterange);
      const dates = expandDates(r);
      const sb = meta.submittedBy || {};
      return {
        id: r.crb5c_fowsessionmakeupid,
        client: r.crb5c_ClientLookup?.crb5c_no || r.crb5c_ClientLookup?.crb5c_name || 'Unknown client',
        typeLabel: r.crb5c_requesttype === 2 ? 'Other document' : 'MC',
        dates: summarise(dates),
        reason: r.crb5c_reason || '',
        submittedBy: [sb.name, sb.email].filter(Boolean).join(' · '),
        submittedOn: dayjs(r.crb5c_newcolumn || r.createdon).format('D MMM YYYY, h:mm A'),
        submittedTs: dayjs(r.crb5c_newcolumn || r.createdon).valueOf(),
        proofPending: meta.proofPending === true,
        open: false,
        estimate: null, estimateLoading: false,
        documents: [], docsLoading: false, docsError: null, docsLoaded: false,
        rejecting: false, rejectReason: '', saving: null, actionError: null, result: null,
      };
    },

    async load() {
      this.loading = true;
      this.loadError = null;
      try {
        // All pending MC / Other requests (swaps never need approval).
        const { data } = await axios.get('/api/makeup-requests?status=0');
        this.cards = (data.data || []).filter((r) => r.crb5c_requesttype !== 0).map(this.toCard);

        // The email's request may already be handled — say so instead of silently dropping it.
        if (this.linkedId && !this.cards.some((c) => c.id === this.linkedId)) {
          try {
            const res = await this.fetchSingle(this.linkedId);
            const r = res?.data || res;
            if (r && r.crb5c_fowsessionmakeupid) {
              const s = r.crb5c_requeststatus;
              this.linkedProcessed = {
                client: r.crb5c_ClientLookup?.crb5c_no || 'this client',
                dates: summarise(expandDates(r)),
                status: s === 1 ? 'approved' : s === 2 ? 'rejected' : r.crb5c_requesttype === 0 ? 'a session swap (auto-applied, no approval needed)' : 'processed',
              };
            }
          } catch (e) { /* ignore — list still shows */ }
        }

        // Estimates for every card (drives the badges + "Approve all"); docs load on expand.
        this.cards.forEach((c) => this.loadEstimate(c));
        const first = this.pendingCards[0];
        if (first) this.toggle(first);
      } catch (e) {
        this.loadError = e?.response?.data?.error || e?.message || 'Could not load pending requests.';
      } finally {
        this.loading = false;
      }
    },

    toggle(c) {
      c.open = !c.open;
      if (c.open && !c.docsLoaded) this.loadDocuments(c);
    },

    async loadEstimate(c) {
      c.estimateLoading = true;
      try { c.estimate = await this.getCreditEstimate(c.id); }
      catch (e) { c.estimate = null; }
      finally { c.estimateLoading = false; }
    },

    async loadDocuments(c) {
      c.docsLoading = true; c.docsError = null;
      try {
        const data = await this.getDocument(c.id);
        c.documents = data?.documents || (data?.downloadUrl ? [data] : []);
      } catch (e) {
        const msg = e?.response?.data?.error;
        c.docsError = msg && !/no document/i.test(msg) ? msg : null;
        c.documents = [];
      } finally { c.docsLoading = false; c.docsLoaded = true; }
    },

    async approve(c) {
      c.saving = 'approve'; c.actionError = null;
      try { await this.approveRequest(c.id); c.result = 'approved'; }
      catch (e) { c.actionError = e?.response?.data?.error || 'Approval failed — please try again.'; }
      finally { c.saving = null; }
    },

    async reject(c) {
      c.saving = 'reject'; c.actionError = null;
      try { await this.rejectRequest({ requestId: c.id, reason: c.rejectReason.trim() }); c.result = 'rejected'; }
      catch (e) { c.actionError = e?.response?.data?.error || 'Rejection failed — please try again.'; }
      finally { c.saving = null; }
    },

    // Approve every pending request this approver is allowed to, one after another.
    async approveAllAllowed() {
      const targets = this.cards.filter((c) => this.canApprove(c) && c.estimate);
      if (!targets.length) return;
      const ok = await this.$bvModal.msgBoxConfirm(
        `Approve ${targets.length} request(s)? Each caregiver will be notified.`,
        { title: 'Approve all', okVariant: 'success', okTitle: 'Approve all', cancelTitle: 'Cancel', centered: true, buttonSize: 'sm' }
      );
      if (!ok) return;
      this.bulkRunning = true;
      for (const c of targets) { await this.approve(c); }
      this.bulkRunning = false;
    },
  },
};
</script>

<style scoped>
.email-approval-page { min-height: 100vh; background: #f4f2f8; padding: 1rem; display: flex; justify-content: center; align-items: flex-start; text-align: left; }
.ea-card { width: 100%; max-width: 620px; background: #fff; border-radius: 16px; box-shadow: 0 4px 18px rgba(80, 39, 107, 0.12); overflow: hidden; }
.ea-header { background: #50276b; color: #fff; padding: 0.9rem 1.1rem; font-weight: 700; font-size: 1.05rem; display: flex; align-items: center; gap: 0.5rem; }
.ea-header-count { margin-left: auto; font-size: 0.8rem; font-weight: 600; background: rgba(255,255,255,0.18); border-radius: 999px; padding: 0.15rem 0.6rem; }
.ea-loading, .ea-error, .ea-empty { padding: 2rem 1rem; text-align: center; }
.ea-loading.small { padding: 0.5rem 0; text-align: left; }
.ea-error { color: #b91c1c; }
.ea-empty { color: #6b7280; }
.ea-bulk { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.7rem 1.1rem; background: #f7f3fb; border-bottom: 1px solid #ece6f3; }
.ea-req { border-bottom: 1px solid #ece6f3; }
.ea-req--linked { box-shadow: inset 4px 0 0 #50276b; }
.ea-req-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.8rem 1.1rem; cursor: pointer; }
.ea-req--open .ea-req-head { background: #faf8fc; }
.ea-req-title { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.ea-req-title strong { font-size: 1rem; }
.ea-req-sub { font-size: 0.8rem; color: #6b7280; }
.ea-req-state { display: flex; align-items: center; gap: 0.4rem; flex-shrink: 0; color: #6b7280; }
.ea-badge { font-size: 0.72rem; font-weight: 700; border-radius: 999px; padding: 0.15rem 0.55rem; white-space: nowrap; }
.ea-badge.ok { background: #d1e7dd; color: #0f5132; }
.ea-badge.bad { background: #f8d7da; color: #842029; }
.ea-badge.warn { background: #fff3cd; color: #7a5a00; }
.ea-badge.cn { background: #e4dcf0; color: #50276b; }
.ea-badge.muted { background: #eef0f2; color: #555; }
.ea-req-body { padding-bottom: 0.4rem; }
.ea-row { display: flex; gap: 0.75rem; padding: 0.45rem 1.1rem; }
.ea-label { flex: 0 0 100px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #8a8a96; padding-top: 0.15rem; }
.ea-value { flex: 1; font-size: 0.92rem; color: #222; word-break: break-word; }
.ea-estimate, .ea-docs { margin: 0.6rem 1.1rem 0; border-radius: 12px; padding: 0.7rem 0.9rem; background: #f7f3fb; font-size: 0.9rem; }
.ea-estimate.muted { color: #6b7280; display: flex; align-items: center; }
.ea-docs { background: #fff8ec; }
.ea-estimate-title { display: flex; align-items: center; gap: 0.35rem; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #6b7280; margin-bottom: 0.4rem; }
.ea-doc { margin-top: 0.6rem; }
.ea-doc-name { display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; margin-bottom: 0.35rem; }
.ea-doc-img { width: 100%; max-height: 70vh; object-fit: contain; border-radius: 10px; border: 1px solid #e5e0ee; background: #fff; }
.ea-doc-pdf { width: 100%; height: 65vh; border: 1px solid #e5e0ee; border-radius: 10px; background: #fff; }
.ea-notice { margin: 0.7rem 1.1rem 0; border-radius: 10px; padding: 0.6rem 0.9rem; display: flex; gap: 0.5rem; align-items: center; font-size: 0.88rem; }
.ea-notice.warning { background: #fff3cd; color: #7a5a00; }
.ea-notice.info { background: #e7f1ff; color: #0a3d7a; margin-top: 0.9rem; }
.ea-reject { padding: 0.7rem 1.1rem 0; }
.ea-actions { display: flex; gap: 0.6rem; padding: 0.8rem 1.1rem 0.6rem; }
.ea-btn { flex: 1; padding: 0.7rem 0.5rem; font-weight: 700; font-size: 1rem; display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem; }
.ea-error-text { color: #b91c1c; font-size: 0.85rem; padding: 0 1.1rem 0.6rem; margin: 0; }
.ea-done-inline { display: flex; align-items: center; gap: 0.5rem; padding: 0.8rem 1.1rem; font-weight: 600; }
.ea-done-inline .approved { color: #198754; font-size: 1.4rem; }
.ea-done-inline .rejected { color: #dc3545; font-size: 1.4rem; }
.ea-footer { padding: 1rem 1.1rem 1.2rem; text-align: center; }
.ea-close { font-weight: 700; color: #50276b; margin: 0.5rem 0; }
@media (max-width: 480px) {
  .email-approval-page { padding: 0; }
  .ea-card { border-radius: 0; min-height: 100vh; }
  .ea-row { flex-direction: column; gap: 0.15rem; }
  .ea-label { flex: none; }
}
</style>
